import { captureError } from '~/modules/pixwel/exception-handler.factory';

/**
 * Handles exceptions:
 * 1) if exception is not an error object, converts it to one
 * 2) captures error in LogRocket
 * 3) logs error to browser console
 * 4) returns error
 *
 * @param {*} e - exception thrown from try block
 * @param {string} prependMsg - message to prepend to error
 * @returns {Object} - error object
 */
export function handleError(e, prependMsg = 'Error') {

	if(!e) {
		return;
	}
	if(e?.status === 401 || e?.response?.status === 401 || e?.data?.error?.code === 401) {
		// 401 is handled globally by the API interceptor (redirect to login).
		// Silently return to avoid duplicate toasts and log noise.
		return e;
	}
	if(e?.status === 403 || e?.response?.status === 403 || e?.data?.error?.code === 403) {
		return e;
	}
	if(e?.status === 404 || e?.response?.status === 404 || e?.data?.error?.code === 404) {
		return e;
	}
	if(e?.status === 422 || e?.response?.status === 422) {
		return e;
	}
	// Aborted or canceled requests (page navigation/unmount mid-flight, e.g. email link
	// scanners tearing down the 2FA login page) are transient, not app errors. Sentry's
	// beforeSend already skips ECONNABORTED but never sees it: the re-wrap below strips
	// the axios error code, so the skip has to happen here.
	if(e?.code === 'ECONNABORTED' || e?.code === 'ERR_CANCELED'
		|| e?.message === 'Request aborted' || e?.message === 'canceled') {
		return e;
	}
	if(e?.status === 405 || e?.response?.status === 405 || e?.data?.error?.code === 405) {
		// 405 is a known error, no need to capture it if message is provided
		if( !e?.data?.error?.message ) {
			captureError(e, 'Unknown Error');
		}
		return;
	}

	let err;

	if(e.error) {
		err = new Error(e.error.message || '');
	}
	else if(e.data) {
		err = e.data.error
			? new Error(e.data.error.message)
			: new Error(e.data.message || '');
	}
	else if(e.response?.data) {
		const errorValue = e.response.data?.error;
		if(errorValue) {
			err = new Error(typeof errorValue === 'string' ? errorValue : (errorValue.message || ''));
		}
		else {
			const msg = e.response.data.message
				|| (typeof e.response.data === 'string' ? e.response.data : '')
				|| e.response.statusText
				|| e.message
				|| '';
			err = new Error(msg);
		}
	}
	else if(e.message) {
		const message = e.message?.message || e.message || '';
		err = new Error(message);
		if(message.includes('404')) {
			return e;
		}
	}
	else {
		err = new Error(e);
	}

	// Ensure a meaningful message so captureError can log and show a toast
	if(!err.message) {
		err = new Error('An unexpected error occurred');
	}

	captureError(err, `${prependMsg}: ${err.message}`);
	return err;
}
